import { useEffect, useState } from "react";
import {
  Alert,
  Backdrop,
  Card,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Icon,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  TableBody,
  TableCell,
  TableRow,
  Table,
  Grid,
} from "@mui/material";
import axios from "axios.js";

import MDBox from "theme/components/MDBox";
import MDTypography from "theme/components/MDTypography";
import DataTable from "theme/Tables/DataTable";
import MDButton from "theme/components/MDButton";
import MDInput from "theme/components/MDInput";

function PricePlansCard({
  campaignRef,
  triggerRefresh,
  packages,
  campaignPackage,
  campaignType,
  currency,
}) {
  const [showLoading, setShowLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [errorMsg, setErrMsg] = useState("");
  const [severity, setSeverity] = useState("");
  const handleClose = () => {
    setOpen(false);
    setErrMsg("");
  };

  const [tableRows, setTableRows] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [selectedPackageObject, setSelectedPackageObject] = useState(null);
  const [packageDuration, setPackageDuration] = useState(1);
  const [openPackageDialog, setOpenPackageDialog] = useState(false);

  async function activateCampaign() {
    if (selectedPackage === null) {
      setSeverity("error");
      setErrMsg("Select a package");
      setOpen(true);
      return;
    }

    if (packageDuration === null) {
      setPackageDuration(1);
    }

    setOpenPackageDialog(false);
    setShowLoading(true);

    const json = {
      campaign: campaignRef,
      package: selectedPackage,
      duration: packageDuration,
    };

    await axios
      .post("portal/campaigns/packages/purchase.json", json)
      .then((result) => {
        setShowLoading(false);
        if (result.data.status === "success") {
          setSeverity("success");
          setErrMsg(result.data.data.message);
          setOpen(true);

          triggerRefresh();
        } else {
          setSeverity("error");
          setErrMsg(result.data.data.message);
          setOpen(true);
        }
      })
      .catch(() => {
        setShowLoading(false);
        setSeverity("error");
        setErrMsg(
          `Unable to complete request. ${
            navigator.onLine ? "" : "Make sure you have an active internet connection"
          }`
        );
        setOpen(true);
      });
  }

  useEffect(() => {}, []);

  useEffect(() => {
    if (selectedPackageObject === null) {
      return;
    }
    if (selectedPackageObject.price === 0) {
      setPackageDuration(1);
    }
  }, [selectedPackageObject]);
  return (
    <MDBox>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={showLoading}
        onClick={handleClose}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <Snackbar
        open={open}
        autoHideDuration={6000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {errorMsg}
        </Alert>
      </Snackbar>
      <Dialog open={openPackageDialog} fullWidth maxWidth="md">
        <DialogTitle>
          <MDBox align="center" mb={1}>
            <MDTypography variant="h6" fontSize={24}>
              Activate Campaign
            </MDTypography>
            <MDTypography variant="h6" fontSize={14} fontWeight="light">
              Choose a subscription plan
            </MDTypography>
          </MDBox>
          <MDBox>
            <Divider />
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox mb={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6} lg={6}>
                <MDBox>
                  <MDBox mt={1}>
                    <FormControl sx={{ minWidth: 120, width: "100%" }}>
                      <InputLabel>Choose Plan</InputLabel>
                      <Select
                        label="Choose Plan"
                        onChange={(e) => {
                          let found = packages.every((pck) => {
                            if (pck.reference === e.target.value) {
                              setSelectedPackageObject(pck);
                              return false;
                            }
                            return true;
                          });
                          setSelectedPackage(e.target.value);
                        }}
                        sx={{ padding: "12px", paddingLeft: "3px" }}
                      >
                        {packages.map((pckg) => (
                          <MenuItem value={pckg.reference}>{pckg.title}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </MDBox>
                  <MDBox mt={5}>
                    <FormControl sx={{ minWidth: 120, width: "100%" }}>
                      <InputLabel>Duration</InputLabel>
                      <Select
                        label="Duration"
                        onChange={(e) => {
                          if (selectedPackageObject.price === 0) {
                            setPackageDuration(1);
                          } else {
                            setPackageDuration(e.target.value);
                          }
                        }}
                        sx={{ padding: "12px", paddingLeft: "3px" }}
                        value={packageDuration}
                      >
                        {[1, 2, 3, 6, 9, 12].map((x) => (
                          <MenuItem value={x}>{`${x} month${x > 1 ? "s" : ""}`}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </MDBox>
                  {selectedPackage && (
                    <MDBox mt={4}>
                      <Table>
                        <TableRow>
                          <TableCell>
                            <MDTypography fontSize={12}>Price</MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography align="right" fontSize={12}>
                              {`${currency}${(
                                (selectedPackageObject.price * packageDuration) /
                                100
                              ).toLocaleString("en", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <MDTypography fontSize={12}>VAT</MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography align="right" fontSize={12}>
                              {`${currency}${(
                                (selectedPackageObject.price * packageDuration * 0.075) /
                                100
                              ).toLocaleString("en", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>
                            <MDTypography fontSize={14} fontWeight="bold">
                              Total
                            </MDTypography>
                          </TableCell>
                          <TableCell>
                            <MDTypography align="right" fontSize={14} fontWeight="bold">
                              {`${currency}${(
                                (selectedPackageObject.price * packageDuration * 0.075 +
                                  selectedPackageObject.price * packageDuration) /
                                100
                              ).toLocaleString("en", {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}`}
                            </MDTypography>
                          </TableCell>
                        </TableRow>
                      </Table>
                    </MDBox>
                  )}
                </MDBox>
              </Grid>
              <Grid item xs={0} md={1} lg={1}>
                <Divider orientation="vertical" flexItem />
              </Grid>
              <Grid item xs={12} md={5} lg={5}>
                <MDBox>
                  {selectedPackageObject ? (
                    <MDBox align="center">
                      <MDBox>
                        <MDTypography variant="h6">{selectedPackageObject.title}</MDTypography>
                        <MDTypography fontSize={14}>{`${currency}${(
                          selectedPackageObject.price / 100
                        ).toLocaleString("en", {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}`}</MDTypography>
                      </MDBox>
                      <Divider />
                      {campaignType === "SALES" ? (
                        <MDBox mt={0}>
                          <Table>
                            <TableRow>
                              <TableCell>
                                <MDTypography>
                                  <MDTypography fontSize={12}>Capped At</MDTypography>
                                </MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography align="right" fontSize={12}>
                                  {(
                                    selectedPackageObject.sales_cap * packageDuration
                                  ).toLocaleString("en", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                <MDTypography fontSize={12}>Overage Fee</MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography align="right" fontSize={12}>
                                  {selectedPackageObject.is_sales_fee_percentage
                                    ? `${(selectedPackageObject.sales_fee * 100).toFixed(2)}%`
                                    : `${currency}${(
                                        selectedPackageObject.sales_fee / 100
                                      ).toLocaleString("en", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`}
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                            {selectedPackageObject.is_sales_fee_percentage && (
                              <>
                                {selectedPackageObject.sales_fee_min && (
                                  <TableRow>
                                    <TableCell>
                                      <MDTypography fontSize={12}>Fee (min)</MDTypography>
                                    </TableCell>
                                    <TableCell>
                                      <MDTypography align="right" fontSize={12}>{`${currency}${(
                                        selectedPackageObject.sales_fee_min / 100
                                      ).toLocaleString("en", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`}</MDTypography>
                                    </TableCell>
                                  </TableRow>
                                )}

                                {selectedPackageObject.sales_fee_max && (
                                  <TableRow>
                                    <TableCell>
                                      <MDTypography fontSize={12}>Fee (max)</MDTypography>
                                    </TableCell>
                                    <TableCell>
                                      <MDTypography align="right" fontSize={12}>{`${currency}${(
                                        selectedPackageObject.sales_fee_max / 100
                                      ).toLocaleString("en", {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}`}</MDTypography>
                                    </TableCell>
                                  </TableRow>
                                )}
                              </>
                            )}
                          </Table>
                        </MDBox>
                      ) : (
                        <MDBox mt={0}>
                          <Table>
                            <TableRow>
                              <TableCell>
                                <MDTypography fontSize={12}>Capped At</MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography align="right" fontSize={12}>
                                  {(
                                    selectedPackageObject.data_cap * packageDuration
                                  ).toLocaleString("en", {
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0,
                                  })}
                                </MDTypography>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                <MDTypography fontSize={12}>Overage</MDTypography>
                              </TableCell>
                              <TableCell>
                                <MDTypography align="right" fontSize={12}>{`${currency}${(
                                  selectedPackageObject.data_fee / 100
                                ).toLocaleString("en", {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}`}</MDTypography>
                              </TableCell>
                            </TableRow>
                          </Table>
                        </MDBox>
                      )}

                      {/* <MDBox mt={3}>
                              <Table>
                                <TableRow>
                                  <TableCell>
                                    <MDTypography fontSize={12}>Expires On</MDTypography>
                                  </TableCell>
                                  <TableCell>
                                    <MDTypography align="right" fontSize={12}>
                                      {new Date(
                                        new Date().setDate(
                                          new Date().getDate() +
                                            packageDuration * selectedPackageObject.duration
                                        )
                                      ).toISOString()}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              </Table>
                            </MDBox> */}
                      <MDBox mt={3}>
                        <Table>
                          <TableRow>
                            <TableCell>
                              <MDTypography fontSize={12}>Duration</MDTypography>
                            </TableCell>
                            <TableCell>
                              <MDTypography align="right" fontSize={12}>
                                {`${packageDuration * selectedPackageObject.duration} days`}
                              </MDTypography>
                            </TableCell>
                          </TableRow>
                        </Table>
                      </MDBox>
                      <MDBox mt={3}>
                        <Alert severity="info">
                          <MDTypography variant="h6" fontSize={12}>
                            Fees apply above plan cap.
                          </MDTypography>
                        </Alert>
                      </MDBox>
                    </MDBox>
                  ) : (
                    <MDBox align="center">
                      <MDTypography variant="h6" fontSize={36}>
                        <Icon>error</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontSize={14}>
                        No plan selected
                      </MDTypography>
                    </MDBox>
                  )}
                </MDBox>
              </Grid>
            </Grid>
          </MDBox>
        </DialogContent>
        <DialogActions>
          <MDButton
            onClick={() => {
              setOpenPackageDialog(false);
            }}
            color="dark"
            variant="outlined"
          >
            Cancel
          </MDButton>
          <MDButton
            color="dark"
            variant="contained"
            onClick={() => {
              activateCampaign();
            }}
          >
            Activate
          </MDButton>
        </DialogActions>
      </Dialog>
      <MDBox mt={3} mb={5}>
        <Card sx={{ height: "100%", marginTop: "0px" }}>
          <MDBox p={2}>
            <MDBox mt={0} align="center">
              {campaignPackage ? (
                <MDBox>
                  {campaignPackage.is_active ? (
                    <MDBox mb={3}>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon color="success">check_circle</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontSize={14} fontWeight="light">
                        Campaign is active
                      </MDTypography>
                    </MDBox>
                  ) : (
                    <MDBox mb={3}>
                      <MDTypography variant="h6" fontSize={48}>
                        <Icon color="error">error</Icon>
                      </MDTypography>
                      <MDTypography variant="h6" fontSize={14} fontWeight="light">
                        Active plan has expired
                      </MDTypography>
                    </MDBox>
                  )}
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="regular" fontSize={12}>
                            Capped At
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="h6" fontWeight="regular" fontSize={12}>
                            Data Overage
                          </MDTypography>
                        </TableCell>
                        {campaignType === "SALES" && (
                          <>
                            {/* <TableCell align="center">
                              <MDTypography variant="h6" fontWeight="regular" fontSize={12}>
                                Sales Cap
                              </MDTypography>
                            </TableCell> */}
                            <TableCell align="center">
                              <MDTypography variant="h6" fontWeight="regular" fontSize={12}>
                                Sales Overage
                              </MDTypography>
                            </TableCell>
                          </>
                        )}
                        <TableCell align="right">
                          <MDTypography variant="h6" fontWeight="regular" fontSize={12}>
                            Expires On
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>
                          <MDTypography variant="h6" fontWeight="light" fontSize={13}>
                            {`${campaignPackage.data_cap.toLocaleString("en", {
                              minimumFractionDigits: 0,
                              maximumFractionDigits: 0,
                            })}`}
                          </MDTypography>
                        </TableCell>
                        <TableCell align="center">
                          <MDTypography variant="h6" fontWeight="light" fontSize={13}>
                            {`${currency}${(campaignPackage.data_fee / 100).toLocaleString("en", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`}
                          </MDTypography>
                        </TableCell>
                        {campaignType === "SALES" && (
                          <>
                            {/* <TableCell align="center">
                              <MDTypography variant="h6" fontWeight="light" fontSize={13}>
                                {`${campaignPackage.sales_cap.toLocaleString("en", {
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                                })}`}
                              </MDTypography>
                            </TableCell> */}
                            <TableCell align="center">
                              {campaignPackage.is_sales_fee_percentage ? (
                                <MDTypography variant="h6" fontWeight="light" fontSize={13}>
                                  {`${(campaignPackage.sales_fee * 100).toFixed(2)}%`}
                                </MDTypography>
                              ) : (
                                <MDTypography variant="h6" fontWeight="light" fontSize={13}>
                                  {`${currency}${(campaignPackage.sales_fee / 100).toLocaleString(
                                    "en",
                                    {
                                      minimumFractionDigits: 2,
                                      maximumFractionDigits: 2,
                                    }
                                  )}`}
                                </MDTypography>
                              )}
                            </TableCell>
                          </>
                        )}
                        <TableCell align="right">
                          <MDTypography variant="h6" fontWeight="light" fontSize={13}>
                            {campaignPackage.expires_on}
                          </MDTypography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <MDBox mt={3} align="right">
                    <MDButton
                      variant="contained"
                      color="dark"
                      startIcon={<Icon>money</Icon>}
                      onClick={() => {
                        if (campaignPackage.is_active) {
                          if (
                            window.confirm(
                              "Your current activation will be cancelled. Proceed?"
                            ) === false
                          ) {
                            return;
                          }
                        }
                        setSelectedPackage(null);
                        setSelectedPackageObject(null);
                        setPackageDuration(1);
                        setOpenPackageDialog(true);
                      }}
                    >
                      {campaignPackage.is_active ? "Change Package" : "Renew Activation"}
                    </MDButton>
                  </MDBox>
                </MDBox>
              ) : (
                <MDBox>
                  <MDTypography variant="h6" fontSize={48}>
                    <Icon color="error">warning</Icon>
                  </MDTypography>
                  <MDTypography variant="h6" fontSize={14} fontWeight="light">
                    Campaign activation is required!
                  </MDTypography>
                  <MDBox>
                    <Divider />
                  </MDBox>
                  <MDBox mt={2} align="center">
                    <MDButton
                      variant="contained"
                      color="dark"
                      startIcon={<Icon>money</Icon>}
                      onClick={() => {
                        setSelectedPackage(null);
                        setSelectedPackageObject(null);
                        setPackageDuration(1);
                        setOpenPackageDialog(true);
                      }}
                    >
                      Activate
                    </MDButton>
                  </MDBox>
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
    </MDBox>
  );
}

export default PricePlansCard;
